/**
 * useViewportMode (#405) — single source of truth for the four
 * tablet/phone × landscape/portrait layout buckets the call view
 * (and DominoGame, and any future activity component) responds to.
 *
 * Modes:
 *   tablet-landscape — width ≥850 in landscape; the only mode where
 *                      we render side-by-side splits (CallStrip on
 *                      the right, focal content on the left).
 *   tablet-portrait  — portrait, width ≥500. CallStrip stacks on top
 *                      as a strip; focal content fills below.
 *   phone-landscape  — landscape, width <850. Same stack as
 *                      tablet-portrait.
 *   phone-portrait   — portrait, width <500. CallStrip becomes a
 *                      compact 80px pill.
 *
 * #1016 — thresholds lowered from 600/900 to 500/850. The production
 * tablet (Samsung Galaxy Tab A9) reports phone-sized CSS widths and was
 * landing in the phone buckets in BOTH orientations: measured 533×759
 * portrait (was phone-portrait) and 893×399 landscape (was
 * phone-landscape), giving it the cramped phone layouts and meaning
 * #852's tablet-readability calibration never actually applied. The A9
 * now lands tablet-portrait (533 ≥ 500) and tablet-landscape (893 ≥ 850).
 * Trade-off: the A9's landscape width overlaps with large phones, so a
 * few large phones in landscape (~850-900w) also get the side-by-side
 * layout — low impact (rare on /app, not broken, and phones ≥900w were
 * already tablet-landscape). A device-identity "paired tablet" signal
 * would separate them perfectly but adds plumbing; thresholds were
 * chosen for simplicity (they work in the installed PWA and a browser
 * tab alike). Regular phones (≤~480w portrait, ≤844w landscape)
 * unaffected.
 *
 * Originally inlined in DominoGame.js for #390 PR C; promoted to a
 * shared hook in #405 so the default call view (TabletApp) and any
 * future media-area splits can reuse the same break-points without
 * drift.
 */
import { useEffect, useState } from 'react';


export function detectViewportMode() {
  if (typeof window === 'undefined') return 'tablet-landscape';
  const w = window.innerWidth;
  const h = window.innerHeight;
  const portrait = h > w;
  // #1016 — thresholds lowered 600→500 / 900→850 so the production tablet
  // (Samsung A9: 533×759 portrait, 893×399 landscape) lands in the tablet
  // buckets instead of the cramped phone ones. See the file header for
  // the full rationale + trade-off.
  if (portrait && w < 500) return 'phone-portrait';
  if (portrait) return 'tablet-portrait';
  // landscape
  if (w >= 850) return 'tablet-landscape';
  return 'phone-landscape';
}


export default function useViewportMode() {
  const [mode, setMode] = useState(detectViewportMode);
  useEffect(() => {
    const onResize = () => setMode(detectViewportMode());
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };
  }, []);
  return mode;
}
